import { ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Inject, Input, OnDestroy, OnInit, Optional, Output, ViewChild, ViewContainerRef } from "@angular/core";
import { ActivatedRoute, ParamMap } from "@angular/router";
import { AuthService, PredefinedUserZone } from "@saliente/common-auth";
import { dateToInt } from "@saliente/library";
import { ToastrService } from "ngx-toastr";
import { of, Subscription } from "rxjs";
import { switchMap } from "rxjs/operators";
import { MicroProfitFormModel } from "../client-forms.models";
import { ClientFormsService } from "../client-forms.service";
import { Align } from "@progress/kendo-angular-popup";

interface HelpLink {
    code: string;
    link: string;
}

const HELP_LINKS: HelpLink[] = [
    { code: 'm2p_angajat',    link: 'https://app.keez.ro/help/client/antreprenor/taxe_impozite.html#conditia-angajatului' },
    { code: 'consultanta80',  link: 'https://app.keez.ro/help/client/faq/modificari_fiscale/modificari_fiscale_2023.html#conditie-micro-venituri-din-consultanta' },
    { code: 'm2p_plafon',     link: 'https://app.keez.ro/help/client/faq/modificari_fiscale/modificari_fiscale_2025.html#plafonul-microintreprinderii-100-000-eur-din-2026' },
    { code: 'intreprinderi',  link: 'https://app.keez.ro/help/client/faq/modificari_fiscale/modificari_fiscale_intreprinderi_legate.html#intreprinderi-legate' },
    { code: 'impozit_venit',  link: 'https://app.keez.ro/help/client/antreprenor/taxe_impozite.html#impozitul-pe-venit' },
    { code: 'impozit_profit', link: 'https://app.keez.ro/help/client/antreprenor/taxe_impozite.html#impozitul-pe-profit' },
];

@Component({
    selector: 'mp2024-form',
    templateUrl: 'mp2024-form.component.html',
    styleUrls: ['mp2024-form.component.scss'],
    standalone: false
})
export class MP2024FormComponent implements OnInit, OnDestroy {
    @Input()
    public isDialog: boolean;
    @Input()
    public clientEidParam: string;
    @Input()
    public instanceParam: string;

    @Output() public close = new EventEmitter();

    //subscriptions
	private mp2024DataSubscription: Subscription;
    
    //models
    public mp2024FormModel: MicroProfitFormModel;

    public clientEid: string;
    public instance: string;

    //booleans
    public isReadOnly: boolean;
    public hasViewRight: boolean;
    public isLoading: boolean;
    public showConfirmationDialog: boolean = false;
    public caenCodesPopupVisible: boolean = false;
    // public isConclusionSet: boolean = false;
    public showCAENQuestion: boolean = false;
    public p2mFormsBeforeDec2024: boolean = true;

    //UI
	public caenCodesPopupAlign: Align = { horizontal: "right", vertical: "bottom" };
	public caedCodesAnchorAlign: Align = { horizontal: "center", vertical: "top" };
	@ViewChild("caenCodesAnchor") public caenCodesAnchor: ElementRef;
	@ViewChild("caenCodesPopup", { read: ElementRef }) public caenCodesPopup: ElementRef;

    get are1FTEDisabled(): boolean {
        return this.isP2M && (this.mp2024FormModel.data.doresteMicro == null || !this.mp2024FormModel.data.doresteMicro);
    }

    get areConsultanta80Disabled(): boolean {
        return this.mp2024FormModel.data.are1FTE == null || !this.mp2024FormModel.data.are1FTE;
    }

    get caSub500kDisabled(): boolean {
        return this.p2mFormsBeforeDec2024 
            ? (this.mp2024FormModel.data.areConsultanta80 == null || this.mp2024FormModel.data.areConsultanta80)
            : (this.mp2024FormModel.data.are1FTE == null || !this.mp2024FormModel.data.are1FTE);
    }

    get are253MicroDisabled(): boolean {
        return this.mp2024FormModel.data.caSub500k == null || !this.mp2024FormModel.data.caSub500k;
    }

    get pastreazaFirmaMicroDisabled(): boolean {
        return this.mp2024FormModel.data.are253Micro == null || !this.mp2024FormModel.data.are253Micro;
    }

    get saveDisabled(): boolean {
        return !this.isConclusionSet;
    }

    get isConclusionSet() : boolean {
        return ((this.mp2024FormModel.data.ramaneMicro || this.mp2024FormModel.data.devineMicro) && (this.mp2024FormModel.data.micro3p ||
            this.mp2024FormModel.data.micro1p)) || this.mp2024FormModel.data.devineProfit  ||
            this.mp2024FormModel.data.ramaneProfit;
    }

    get isP2M(): boolean {
        return this.mp2024FormModel.formDataParameters.schimbare == "p2m";
    }

    get isM2P(): boolean {
        return this.mp2024FormModel.formDataParameters.schimbare == "m2p";
    }

    get plafonParam(): string {
        return this.p2mFormsBeforeDec2024 ? "500 000" : this.mp2024FormModel.formDataParameters.plafon.toString();
    }

    get isFormExpired(): boolean {
        if (!this.mp2024FormModel?.formData?.expires) return false;
        return dateToInt(new Date()) > this.mp2024FormModel.formData.expires;
    }

    constructor(private clientFormsService: ClientFormsService, private authService: AuthService, private route: ActivatedRoute, private toastr: ToastrService,
        private cdr: ChangeDetectorRef,
        @Optional() @Inject('instance') public instanceRouteParam: string) {}

    ngOnInit(): void {
        this.isLoading = true;
        this.mp2024DataSubscription = this.route.paramMap
            .pipe(
                switchMap((params: ParamMap) => {
                    this.clientEid = this.isDialog ? this.clientEidParam : params.get('id');
                    this.instance = this.isDialog ? this.instanceParam : this.instanceRouteParam;

                    return this.clientFormsService.getMicroProfitData(this.clientEid, this.instance);
                })
		    )
			.subscribe(async (mp2024FormData: MicroProfitFormModel) => {
                if (mp2024FormData) {
                    this.isReadOnly = this.authService.selectedZoneCode == PredefinedUserZone.Expert
                        || mp2024FormData.formData.status == 'Submitted'
                        || (!!mp2024FormData.formData.expires && dateToInt(new Date()) > mp2024FormData.formData.expires);

                    this.hasViewRight = this.authService.isInRole('EXPERT') || this.authService.isInRole('ZEUS') 
                        || this.authService.isInRole(mp2024FormData.formData.displayRole);

                    if (this.hasViewRight) {
                        this.mp2024FormModel = mp2024FormData;

                        if (mp2024FormData.formDataParameters.luna >= 202412) {
                            let profitMicroCode = await this.clientFormsService.getClientFiscalInfoSummaryForMonth(this.clientEid, mp2024FormData.formDataParameters.luna);
                            if (profitMicroCode == 'Micro3') {
                                this.showCAENQuestion = false;
                                this.mp2024FormModel.data.hasCaenCode = true;
                            }
                            else {
                                this.showCAENQuestion = true;
                                this.mp2024FormModel.data.hasCaenCode = false;
                            }
                        }

                        if (this.mp2024FormModel.data.micro1p != null && this.mp2024FormModel.data.micro1p) {
                            this.mp2024FormModel.data.hasCaenCode = false;
                        }

                        if (this.mp2024FormModel.data.micro3p != null && this.mp2024FormModel.data.micro3p) {
                            this.mp2024FormModel.data.hasCaenCode = true;
                        }

                        // AM. 15.01.2025, task 10001
                        this.p2mFormsBeforeDec2024 = (mp2024FormData.formDataParameters.luna >= 202412 && mp2024FormData.formDataParameters.schimbare == "p2m") ? false : true;
                        
                        this.initializeConclusion();

                        if (this.mp2024FormModel.formData.status != 'Submitted') {
                            this.computeBooleans();
                        }

                        this.getMP2024FormConclusion();
                    }
                }
                else {
                    this.mp2024FormModel = null;
                }

                this.isLoading = false;

                setTimeout(() => {
                    this.cdr.detectChanges();
                }, 200);
		});
    }

    ngOnDestroy(): void {
        if (this.mp2024DataSubscription) { this.mp2024DataSubscription.unsubscribe(); this.mp2024DataSubscription = null; }
    }

    private getHelpLink(code: string): string {
        return HELP_LINKS.find(h => h.code === code)?.link ?? '';
    }

    public onAre1FTEHelp() {
        window.open(this.getHelpLink('m2p_angajat'), '_blank', 'noopener noreferrer');
    }

    public onAreConsultanta80() {
        window.open(this.getHelpLink('consultanta80'), '_blank', 'noopener noreferrer');
    }

    public onCaSub500k() {
        window.open(this.getHelpLink('intreprinderi'), '_blank', 'noopener noreferrer');
    }

    public onAre253Micro() {
        window.open(this.getHelpLink('m2p_plafon'), '_blank', 'noopener noreferrer');
    }

    public onRamaneMicroHelp() {
        const code = this.mp2024FormModel.data.ramaneMicro ? 'impozit_venit' : 'impozit_profit';
        window.open(this.getHelpLink(code), '_blank', 'noopener noreferrer');
    }

    public showCaenCodes() {
		this.caenCodesPopupVisible = !this.caenCodesPopupVisible;
    }

    public computeBooleans() {
        if (this.isP2M) {
            this.onDoresteMicroChange();
        }

        this.onAre1FTEChange();
        this.onAreConsultanta80Change();
        this.onAre253MicroChange();
        this.onPastreazaFirmaMicroChange();
    }

    public initializeConclusion() {
        this.mp2024FormModel.data.ramaneMicro = this.mp2024FormModel.data.ramaneMicro == null ? false : this.mp2024FormModel.data.ramaneMicro;
        this.mp2024FormModel.data.devineProfit = this.mp2024FormModel.data.devineProfit == null ? false : this.mp2024FormModel.data.devineProfit;
        this.mp2024FormModel.data.devineMicro = this.mp2024FormModel.data.devineMicro == null ? false : this.mp2024FormModel.data.devineMicro;
        this.mp2024FormModel.data.ramaneProfit = this.mp2024FormModel.data.ramaneProfit == null ? false : this.mp2024FormModel.data.ramaneProfit;

        if (!this.showCAENQuestion && (this.mp2024FormModel.data.ramaneMicro || this.mp2024FormModel.data.devineMicro)) {
            this.mp2024FormModel.data.micro3p = true;
            this.mp2024FormModel.data.micro1p = false;
        }
    }

    public noConclusion() {
        this.mp2024FormModel.data.ramaneMicro = false;
        this.mp2024FormModel.data.devineProfit = false;
        this.mp2024FormModel.data.devineMicro = false;
        this.mp2024FormModel.data.ramaneProfit = false;

        // this.isConclusionSet = false;
    }

    public ramaneMicro() {
        this.mp2024FormModel.data.ramaneMicro = true;
        this.mp2024FormModel.data.devineProfit = false;
        this.mp2024FormModel.data.devineMicro = false;
        this.mp2024FormModel.data.ramaneProfit = false;

        // this.isConclusionSet = false;
    }

    public devineMicro() {
        this.mp2024FormModel.data.ramaneMicro = false;
        this.mp2024FormModel.data.devineProfit = false;
        this.mp2024FormModel.data.devineMicro = true;
        this.mp2024FormModel.data.ramaneProfit = false;

        // this.isConclusionSet = false;
    }

    public ramaneProfit() {
        this.mp2024FormModel.data.ramaneMicro = false;
        this.mp2024FormModel.data.devineProfit = false;
        this.mp2024FormModel.data.devineMicro = false;
        this.mp2024FormModel.data.ramaneProfit = true;

        // this.isConclusionSet = true;
    }

    public devineProfit() {
        this.mp2024FormModel.data.ramaneMicro = false;
        this.mp2024FormModel.data.devineProfit = true;
        this.mp2024FormModel.data.devineMicro = false;
        this.mp2024FormModel.data.ramaneProfit = false;

        // this.isConclusionSet = true;
    }

    public resetAnswersFromQuestionBelow(fromQuestionBelow: string) {
        if (fromQuestionBelow == "DoresteMicro") {
            this.mp2024FormModel.data.are1FTE = null;
            this.mp2024FormModel.data.areConsultanta80 = null;
            this.mp2024FormModel.data.caSub500k = null;
            this.mp2024FormModel.data.are253Micro = null;
            this.mp2024FormModel.data.pastreazaFirmaMicro = null;
            this.mp2024FormModel.data.hasCaenCode = null;
            this.mp2024FormModel.data.micro1p = null;
            this.mp2024FormModel.data.micro3p = null;
        }
        if (fromQuestionBelow == "Are1FTE") {
            this.mp2024FormModel.data.areConsultanta80 = null;
            this.mp2024FormModel.data.caSub500k = null;
            this.mp2024FormModel.data.are253Micro = null;
            this.mp2024FormModel.data.pastreazaFirmaMicro = null;
            this.mp2024FormModel.data.hasCaenCode = null;
            this.mp2024FormModel.data.micro1p = null;
            this.mp2024FormModel.data.micro3p = null;
        }
        if (fromQuestionBelow == "AreConsultanta80") {
            this.mp2024FormModel.data.caSub500k = null;
            this.mp2024FormModel.data.are253Micro = null;
            this.mp2024FormModel.data.pastreazaFirmaMicro = null;
            this.mp2024FormModel.data.hasCaenCode = null;
            this.mp2024FormModel.data.micro1p = null;
            this.mp2024FormModel.data.micro3p = null;
        }
        if (fromQuestionBelow == "CaSub500k") {
            this.mp2024FormModel.data.are253Micro = null;
            this.mp2024FormModel.data.pastreazaFirmaMicro = null;
            this.mp2024FormModel.data.hasCaenCode = null;
            this.mp2024FormModel.data.micro1p = null;
            this.mp2024FormModel.data.micro3p = null;
        }
        if (fromQuestionBelow == "Are253Micro") {
            this.mp2024FormModel.data.pastreazaFirmaMicro = null;
            this.mp2024FormModel.data.hasCaenCode = null;
            this.mp2024FormModel.data.micro1p = null;
            this.mp2024FormModel.data.micro3p = null;
        }
        if (fromQuestionBelow == "PastreazaFirmaMicro") {
            this.mp2024FormModel.data.hasCaenCode = null;
            this.mp2024FormModel.data.micro1p = null;
            this.mp2024FormModel.data.micro3p = null;
        }
    }

    public onDoresteMicroChange() {
        if (this.mp2024FormModel.data.doresteMicro == null) 
            return;

        if (this.mp2024FormModel.data.doresteMicro) { // answer: yes - no conclusion yet, need to answer the following questions
           this.noConclusion();
        }
        else { // answer: no - there is no need for the other questions; conclusion: ramaneProfit
            this.ramaneProfit();

            this.resetAnswersFromQuestionBelow("DoresteMicro");
        }
    }

    public onAre1FTEChange() {
        if (this.mp2024FormModel.data.are1FTE == null) 
            return;

        if (this.mp2024FormModel.data.are1FTE) { // answer: yes - no conclusion yet, need to answer the following questions
           this.noConclusion();
        }
        else { // answer: no - there is no need for the other questions: devineProfit / ramaneProfit
            if (this.isM2P) {
                this.devineProfit();
            }
            if (this.isP2M) {
                this.ramaneProfit();
            }

            this.resetAnswersFromQuestionBelow("Are1FTE");
        }
    }

    public onAreConsultanta80Change() {
        if (this.mp2024FormModel.data.areConsultanta80 == null) 
            return;

        if (this.mp2024FormModel.data.areConsultanta80) { // answer: yes - there is no need for the following questions: devineProfit / ramaneProfit
            if (this.isM2P) {
                this.devineProfit();
            }
            if (this.isP2M) {
                this.ramaneProfit();
            }

            this.resetAnswersFromQuestionBelow("AreConsultanta80");
        }
        else { // answer: no - no conclusion yet, need to answer the following questions
            this.noConclusion();
        }
    }

    public onCaSub500kChange() { 
        if (this.mp2024FormModel.data.caSub500k == null) 
            return;

        if (this.mp2024FormModel.data.caSub500k) { // answer: yes - no conclusion yet, need to answer the following questions
            this.noConclusion();
        }
        else { // answer: no - there is no need for the following questions: devineProfit / ramaneProfit
            if (this.isM2P) {
                this.devineProfit();
            }
            if (this.isP2M) {
                this.ramaneProfit();
            }
            
            this.resetAnswersFromQuestionBelow("CaSub500k");
        }
    }

    public onAre253MicroChange() {
        if (this.mp2024FormModel.data.are253Micro == null) 
            return;

        if (this.mp2024FormModel.data.are253Micro) { // answer: yes - no conclusion yet, need to answer the following questions
            this.noConclusion();
        }
        else { // answer: no - there is no need for the following questions: ramaneMicro / devineMicro
            if (this.isM2P) {
                this.ramaneMicro();
            }
            if (this.isP2M) {
                this.devineMicro();
            }

            this.resetAnswersFromQuestionBelow("Are253Micro");

            this.mp2024FormModel.data.pastreazaFirmaMicro = true;

            if (!this.showCAENQuestion) {
                this.mp2024FormModel.data.micro3p = true;
                this.mp2024FormModel.data.micro1p = false;
            }
        }
    }

    public onPastreazaFirmaMicroChange() {
        if (this.mp2024FormModel.data.pastreazaFirmaMicro == null) 
            return;

        if (this.mp2024FormModel.data.pastreazaFirmaMicro) { // answer: yes - ramaneMicro / devineMicro
            if (this.isM2P) {
                this.ramaneMicro();
            }
            if (this.isP2M) {
                this.devineMicro();
            }

            if (!this.showCAENQuestion) {
                this.mp2024FormModel.data.micro3p = true;
                this.mp2024FormModel.data.micro1p = false;

                this.mp2024FormModel.data.pastreazaFirmaMicro = true;
            }
        }
        else { // answer: no - devineProfit / ramaneProfit
            if (this.isM2P) {
                this.devineProfit();
            }
            if (this.isP2M) {
                this.ramaneProfit();
            }

            this.resetAnswersFromQuestionBelow("PastreazaFirmaMicro");
        }
    }

    public onAreCodCaenChange() {
        if (this.mp2024FormModel.data.hasCaenCode) {
            this.mp2024FormModel.data.micro3p = true;
            this.mp2024FormModel.data.micro1p = false;
        }
        else {
            this.mp2024FormModel.data.micro1p = true;
            this.mp2024FormModel.data.micro3p = false;
        }

        // this.isConclusionSet = true;
    }
    
    public getMP2024FormConclusion() {
        if (this.mp2024FormModel.data.ramaneMicro) {
            if (!this.showCAENQuestion)
                return 'Firma va rămâne Microîntreprindere - plătitoare de impozit pe venit 3%.';
            return 'Firma va rămâne Microîntreprindere - plătitoare de impozit pe venit.';
        }
        else if (this.mp2024FormModel.data.devineMicro) {
            return 'Firma va deveni Microîntreprindere - plătitoare de impozit pe venit.'
        }
        else if (this.mp2024FormModel.data.ramaneProfit) {
            return 'Firma va ramane plătitoare de impozit pe profit.';
        }
        else if (this.mp2024FormModel.data.devineProfit) {
            return 'Firma va deveni plătitoare de impozit pe profit.';
        }
        else {
            return null;
        }
    }

    public getMP2024ProcentVenitConclusion() {
        if (this.mp2024FormModel.data.micro1p) {
            return 'Microîntreprindere - plătitoare de impozit pe venit 1%.';
        }
        else if (this.mp2024FormModel.data.micro3p) {
            return 'Microîntreprindere - plătitoare de impozit pe venit 3%.'
        }
        else {
            return null;
        }
    }

    public openConfirmationDialog() {
        this.showConfirmationDialog = true;
    }

    public closeConfirmationDialog() {
        this.showConfirmationDialog = false;
    }

    public async saveMicroProfitForm() {
        let result = await this.clientFormsService.updateClientFormMicroProfitData(this.clientEid, this.instance, this.mp2024FormModel.data);
        if (result) {
            if (!this.isDialog) {
                this.mp2024FormModel.formData.status = "Submitted";
                this.isReadOnly = this.authService.selectedZoneCode == PredefinedUserZone.Expert || this.mp2024FormModel.formData.status == 'Submitted';
            }
            
            this.cancelMicroProfitForm();
            this.toastr.success("Formularul a fost salvat cu succes!");
        }
    }

    public cancelMicroProfitForm() {
        this.closeConfirmationDialog();

        if (this.isDialog) {
            this.close.emit();
        }
    }

    @HostListener("document:click", ["$event"])
	public documentClick(event: any): void {
		if (!this.contains(event.target)) {
			this.caenCodesPopupVisible = false;
		}
	}

	private contains(target: any): boolean {
		return (
		  (this.caenCodesAnchor ? this.caenCodesAnchor.nativeElement.contains(target) : false) ||
		  (this.caenCodesPopup ? this.caenCodesPopup.nativeElement.contains(target) : false)
		);
	}
}
